import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchTenantById } from "../../../api/admin/config/endpoints-referrers";
import { fetchUser } from "../../../api/users/endpoints-referrers";
import { AuthTemplate } from "../../../components/auth-template/auth-template.comp";
import { LoaderView } from "../../../components/loader/loader.comp";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";

/**
 * Handles registration success redirects from email verification
 * Fetches the tenant URL by tenantId and redirects to it
 * Falls back to fetching tenantId from authenticated user if not in URL
 */
const RegistrationSuccess = () => {
    const [searchParams] = useSearchParams();
    const [error, setError] = useState<string>("");
    const { isAuthenticatedState } = FirebaseAuthContainer.useContainer();

    useEffect(() => {
        const handleRedirect = async () => {
            try {
                let tenantId = searchParams.get("tenantId");

                // If no tenantId in URL, try to get it from authenticated user
                if (!tenantId && isAuthenticatedState) {
                    try {
                        const { data: user } = await fetchUser();
                        tenantId = user.tenantId;
                    } catch (err) {
                        console.error("[RegistrationSuccess] Failed to fetch user:", err);
                    }
                }

                if (!tenantId) {
                    setError("Missing tenant ID. Please contact support.");
                    return;
                }

                // Fetch tenant URL from backend
                const {
                    data: { url }
                } = await fetchTenantById(tenantId);

                if (!url) {
                    setError("Tenant URL not found. Please contact support.");
                    return;
                }

                // Redirect to login page with returnUrl and tenantOrigin
                // This allows the user to complete authentication flow
                const loginUrl = new URL("/login", window.location.origin);
                loginUrl.searchParams.set("returnUrl", url);
                loginUrl.searchParams.set("tenantOrigin", url);

                window.location.href = loginUrl.toString();
            } catch (err: any) {
                console.error("[RegistrationSuccess] Error fetching tenant URL:", err);
                setError("Failed to redirect to your account. Please contact support.");
            }
        };

        handleRedirect();
    }, [searchParams, isAuthenticatedState]);

    // Show error if redirect failed
    if (error) {
        return (
            <AuthTemplate>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "40px 20px",
                        textAlign: "center"
                    }}
                >
                    <div
                        style={{
                            marginBottom: "20px",
                            fontSize: "24px",
                            fontWeight: 600,
                            color: "#d32f2f"
                        }}
                    >
                        Registration Error
                    </div>
                    <div
                        style={{
                            marginBottom: "30px",
                            fontSize: "16px",
                            color: "#666",
                            maxWidth: "500px",
                            lineHeight: "1.6"
                        }}
                    >
                        {error}
                    </div>
                </div>
            </AuthTemplate>
        );
    }

    // Show loading while processing redirect
    return (
        <AuthTemplate>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "40px 20px",
                    textAlign: "center"
                }}
            >
                <div
                    style={{
                        marginBottom: "20px",
                        fontSize: "24px",
                        fontWeight: 600
                    }}
                >
                    Registration Successful!
                </div>
                <div
                    style={{
                        marginBottom: "30px",
                        fontSize: "16px",
                        color: "#666",
                        maxWidth: "500px",
                        lineHeight: "1.6"
                    }}
                >
                    Your email has been verified. Redirecting you to your account...
                </div>
                <LoaderView show={true} />
            </div>
        </AuthTemplate>
    );
};

export default RegistrationSuccess;
